import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { gsap } from 'gsap';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { CustomMediaSlide } from '@/types';
import { getUploadUrl } from '@/config/api';
import { ANIMATION_CONFIG } from '@/config/animations';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';
import TextOverlay from './TextOverlay';
import { useCursorContext } from '@/providers/CursorProvider';
import RotateDeviceIndicator from '@/components/RotateDeviceIndicator';

interface CustomMediaSectionProps {
    slide: CustomMediaSlide;
    isActive?: boolean;
    onZoomChange?: (zoomed: boolean) => void;
}

const CustomMediaSection: React.FC<CustomMediaSectionProps> = ({ slide, isActive, onZoomChange }) => {
    const mediaRef = useRef<HTMLDivElement>(null);
    const hasAnimated = useRef(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isInNavigationZone, setIsInNavigationZone] = useState(false);
    const { setCursorType } = useCursorContext();

    const handleImageClick = (e: React.MouseEvent) => {
        // Only navigate on a genuine click (not after a touch drag/swipe)
        if (slide.image_url && !isDragging) {
            window.open(slide.image_url, '_blank', 'noopener,noreferrer');
        }
    };

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // No animation - just show content immediately when active
        if (mediaRef.current) {
            // gsap.set(mediaRef.current, { y: 0, opacity: 1 });
        }
    }, [isActive]);

    // Handle zoom state tracking
    const handleTransform = useCallback((ref: ReactZoomPanPinchRef, state: { scale: number; positionX: number; positionY: number }) => {
        const zoomed = state.scale > 1.1;
        setIsZoomed(zoomed);
        setIsDragging(state.scale > 1.1 || Math.abs(state.positionX) > 5 || Math.abs(state.positionY) > 5);
        onZoomChange?.(zoomed);
    }, [onZoomChange]);

    const renderMedia = () => {
        if (slide.media_type === 'video') {
            if (slide.is_external_video && slide.video_url) {
                const embedUrl = getEmbedUrl(slide.video_url, slide.video_platform);
                return (
                    <iframe
                        src={embedUrl}
                        title={slide.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            pointerEvents: 'none'
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                );
            } else if (slide.media?.url) {
                return (
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                            backgroundColor: slide.background_color || '#000'
                        }}
                    >
                        <source src={getUploadUrl(slide.media.url)} type="video/mp4" />
                    </video>
                );
            }
        } else if (slide.media_type === 'image' && slide.media?.url) {
            // Only enable zoom on mobile devices
            if (isMobile) {
                // Use fit_variant to determine objectFit on mobile
                const fitVariant = slide.fit_variant || 'fill';
                let objectFit: 'cover' | 'contain' = 'cover';
                
                if (fitVariant === 'horizontal' || fitVariant === 'vertical' || fitVariant === 'contained') {
                    // Contain for horizontal, vertical, and contained fit
                    objectFit = 'contain';
                } else {
                    // Cover for fill
                    objectFit = 'cover';
                }
                
                return (
                    <TransformWrapper
                        initialScale={1}
                        minScale={1.0}
                        maxScale={4}
                        centerOnInit={true}
                        onTransformed={handleTransform}
                        doubleClick={{ disabled: false }}
                        wheel={{ disabled: false }}
                        panning={{ disabled: isZoomed ? false : true }}
                        velocityAnimation={{ disabled: true }}
                    >
                        <TransformComponent>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                {slide.image_url ? (
                                    <div onClick={handleImageClick} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', pointerEvents: isZoomed ? 'none' : 'auto' }}>
                                        <ImageWithSkeleton
                                            src={getUploadUrl(slide.media.url)}
                                            alt={slide.alt_text || slide.title}
                                            loading={isActive ? "eager" : "lazy"}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: objectFit,
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <ImageWithSkeleton
                                        src={getUploadUrl(slide.media.url)}
                                        alt={slide.alt_text || slide.title}
                                        loading={isActive ? "eager" : "lazy"}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: objectFit,
                                            pointerEvents: 'none'
                                        }}
                                    />
                                )}
                            </div>
                        </TransformComponent>
                    </TransformWrapper>
                );
            } else {
                // Desktop: also use fit_variant
                const fitVariant = slide.fit_variant || 'fill';
                let objectFit: 'cover' | 'contain' = 'cover';
                
                if (fitVariant === 'horizontal' || fitVariant === 'vertical' || fitVariant === 'contained') {
                    // Contain for horizontal, vertical, and contained fit
                    objectFit = 'contain';
                } else {
                    // Cover for fill
                    objectFit = 'cover';
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        {slide.image_url ? (
                            <div onClick={handleImageClick} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                                <ImageWithSkeleton
                                    src={getUploadUrl(slide.media.url)}
                                    alt={slide.alt_text || slide.title}
                                    loading={isActive ? "eager" : "lazy"}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: objectFit,
                                        pointerEvents: 'none'
                                    }}
                                />
                            </div>
                        ) : (
                            <ImageWithSkeleton
                                src={getUploadUrl(slide.media.url)}
                                alt={slide.alt_text || slide.title}
                                loading={isActive ? "eager" : "lazy"}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: objectFit,
                                    pointerEvents: 'none'
                                }}
                            />
                        )}
                    </div>
                );
            }
        }
        return null;
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100vh',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: slide.background_color || '#000'
            }}
        >
            <Box
                className="media-container"
                ref={mediaRef}
                sx={{
                    width: {
                        xs: `${slide.mobile_width_percent ?? 100}%`,
                        md: `${slide.width_percent}%`
                    },
                    height: {
                        xs: `${slide.mobile_height_percent ?? 100}%`,
                        md: `${slide.height_percent}%`
                    },
                    position: 'absolute',
                    left: { xs: '50%', md: `${slide.position_x_percent}%` },
                    top: { xs: '50%', md: `${slide.position_y_percent}%` },
                    transform: 'translate(-50%, -50%)',
                    overflow: 'hidden',
                }}
            >
                {renderMedia()}

                {/* Navigation Zones - only capture hover, let clicks pass through */}
                <Box
                    onMouseEnter={() => {
                        setIsInNavigationZone(true);
                        setCursorType('default');
                    }}
                    onMouseLeave={() => setIsInNavigationZone(false)}
                    sx={{ position: 'absolute', left: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'none' }}
                />
                <Box
                    onMouseEnter={() => {
                        setIsInNavigationZone(true);
                        setCursorType('default');
                    }}
                    onMouseLeave={() => setIsInNavigationZone(false)}
                    sx={{ position: 'absolute', right: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'none' }}
                />
                <Box
                    onMouseEnter={() => {
                        setIsInNavigationZone(true);
                        setCursorType('default');
                    }}
                    onMouseLeave={() => setIsInNavigationZone(false)}
                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'none' }}
                />
                <Box
                    onMouseEnter={() => {
                        setIsInNavigationZone(true);
                        setCursorType('default');
                    }}
                    onMouseLeave={() => setIsInNavigationZone(false)}
                    sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'none' }}
                />
            </Box>

            {(isMobile ? (slide.mobile_text_content || '') : slide.text_content) && (
                <TextOverlay
                    text_content={isMobile ? (slide.mobile_text_content || '') : (slide.text_content || '')}
                    background_color={slide.background_color}
                    isActive={isActive}
                />
            )}
            
            <RotateDeviceIndicator show={slide.enable_rotate && isActive} />
        </Box>
    );
};

// Helper function to get embed URLs
const getEmbedUrl = (url: string, platform?: string): string => {
    if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = extractYouTubeId(url);
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1`;
    } else if (platform === 'vimeo' || url.includes('vimeo.com')) {
        const videoId = extractVimeoId(url);
        return `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
    }
    return url;
};

const extractYouTubeId = (url: string): string => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : '';
};

const extractVimeoId = (url: string): string => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : '';
};

export default CustomMediaSection;
